import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  exchangeLinkedInCode,
  readStoredState,
  readReturnTo,
  clearOAuthState,
} from "@/lib/linkedin";

type Status = "exchanging" | "success" | "error";

export default function LinkedInCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>("exchanging");
  const [message, setMessage] = useState<string>("Connecting your LinkedIn account…");

  useEffect(() => {
    const code = params.get("code");
    const state = params.get("state");
    const oauthError = params.get("error");
    const oauthErrorDescription = params.get("error_description");

    if (oauthError) {
      setStatus("error");
      setMessage(oauthErrorDescription || `LinkedIn returned: ${oauthError}`);
      return;
    }

    if (!code) {
      setStatus("error");
      setMessage("LinkedIn did not return an authorization code.");
      return;
    }

    const storedState = readStoredState();
    if (!state || !storedState || state !== storedState) {
      setStatus("error");
      setMessage("Security check failed (state mismatch). Please try connecting again.");
      return;
    }

    const returnTo = readReturnTo();
    clearOAuthState();

    (async () => {
      const { data, error } = await exchangeLinkedInCode(code);
      if (error || !data?.ok) {
        const detail = (data as { error?: string })?.error || error?.message || "Unknown error";
        setStatus("error");
        setMessage(`Couldn't finish LinkedIn sign-in: ${detail}`);
        return;
      }
      setStatus("success");
      setMessage(`Connected ${data.profile?.name ?? "your LinkedIn account"}.`);
      setTimeout(() => navigate(returnTo, { replace: true }), 900);
    })();
  }, [params, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="max-w-sm w-full rounded-3xl border border-border bg-card p-8 text-center space-y-4 shadow-sm">
        {status === "exchanging" && <Loader2 className="h-10 w-10 mx-auto text-primary animate-spin" />}
        {status === "success" && <CheckCircle2 className="h-10 w-10 mx-auto text-primary" />}
        {status === "error" && <AlertCircle className="h-10 w-10 mx-auto text-destructive" />}
        <h1 className="font-display text-xl font-semibold text-foreground">
          {status === "success" ? "LinkedIn connected" : status === "error" ? "Connection failed" : "Finishing up"}
        </h1>
        <p className="text-sm text-muted-foreground">{message}</p>
        {status === "error" && (
          <Button onClick={() => navigate("/settings", { replace: true })} className="w-full">
            Back to Settings
          </Button>
        )}
      </div>
    </div>
  );
}
